'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Landmark, ArrowRight, Shield, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';


const DEMO_ACCOUNTS = [
  { label: 'Investor', email: 'investor@crowdvault.demo', role: 'investor' },
  { label: 'Issuer', email: 'issuer@crowdvault.demo', role: 'issuer' },
  { label: 'Compliance', email: 'compliance@crowdvault.demo', role: 'compliance' },
  { label: 'Finance', email: 'finance@crowdvault.demo', role: 'finance' },
  { label: 'Admin', email: 'admin@crowdvault.demo', role: 'admin' },
];

export default function LoginPage() {
  const router = useRouter();
  const { signIn, resetPassword, getPortalRoute } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      router.push(getPortalRoute());
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setError('');
    setLoading(true);
    try {
      await resetPassword(forgotEmail);
      setForgotSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (demoEmail: string) => {
    setEmail(demoEmail);
    setPassword('Demo1234!');
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex flex-col">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-60 -right-60 w-[700px] h-[700px] rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #10b981 0%, transparent 65%)' }} />
        <div className="absolute -bottom-60 -left-60 w-[600px] h-[600px] rounded-full opacity-6"
          style={{ background: 'radial-gradient(circle, #06b6d4 0%, transparent 65%)' }} />
        <div className="absolute inset-0 opacity-[0.025]"
          style={{ backgroundImage: 'linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-[#1e293b]/60 bg-[#0a0f1e]/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing" className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
              <Landmark className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold text-white tracking-tight">CrowdVault<sup className="text-emerald-400 text-[10px] ml-0.5">™</sup></span>
          </Link>
          <div className="text-sm text-[#64748b]">
            New investor?{' '}
            <Link href="/onboarding" className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium">Apply now</Link>
          </div>
        </div>
      </header>

      {/* Main */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Logo mark */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-emerald-900/30 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
              <Landmark className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">
              {forgotMode ? 'Reset Password' : 'Welcome Back'}
            </h1>
            <p className="text-sm text-[#64748b]">
              {forgotMode
                ? 'Enter your email to receive a reset link' :'Sign in to your CrowdVault investor account'}
            </p>
          </div>

          {/* Card */}
          <div className="bg-[#111827] border border-[#1e293b] rounded-2xl p-8">
            {/* Forgot password sent */}
            {forgotSent ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-emerald-900/40 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">Check Your Email</h3>
                <p className="text-sm text-[#64748b] mb-6">
                  We&apos;ve sent a password reset link to <span className="text-[#94a3b8]">{forgotEmail}</span>. The link expires in 30 minutes.
                </p>
                <button onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); setError(''); }}
                  className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors">
                  Back to sign in
                </button>
              </div>
            ) : forgotMode ? (
              /* Forgot password form */
              <form onSubmit={handleForgot} className="space-y-5">
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-900/20 border border-red-800/40 text-sm text-red-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-2">Email Address</label>
                  <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                    placeholder="your@email.com" required
                    className="w-full bg-[#1a2236] border border-[#1e293b] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-500/50 transition-colors placeholder:text-[#475569]" />
                </div>
                <button type="submit" disabled={loading || !forgotEmail}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all">
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <><ArrowRight className="w-4 h-4" /> Send Reset Link</>
                  )}
                </button>
                <button type="button" onClick={() => { setForgotMode(false); setError(''); }}
                  className="w-full text-sm text-[#64748b] hover:text-[#94a3b8] transition-colors py-2">
                  Back to sign in
                </button>
              </form>
            ) : (
              /* Login form */
              <form onSubmit={handleLogin} className="space-y-5">
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-900/20 border border-red-800/40 text-sm text-red-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-2">Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com" required autoComplete="email"
                    className="w-full bg-[#1a2236] border border-[#1e293b] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-500/50 transition-colors placeholder:text-[#475569]" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wider">Password</label>
                    <button type="button" onClick={() => { setForgotMode(true); setError(''); }}
                      className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password" required autoComplete="current-password"
                      className="w-full bg-[#1a2236] border border-[#1e293b] rounded-xl px-4 py-3 text-sm text-white pr-12 focus:outline-none focus:border-emerald-500/50 transition-colors placeholder:text-[#475569]" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#94a3b8] transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/20">
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <><Lock className="w-4 h-4" /> Sign In Securely</>
                  )}
                </button>

                {/* Demo accounts */}
                <div>
                  <div className="relative flex items-center gap-3 py-1 mb-3">
                    <div className="flex-1 h-px bg-[#1e293b]" />
                    <span className="text-xs text-[#475569]">demo accounts</span>
                    <div className="flex-1 h-px bg-[#1e293b]" />
                  </div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {DEMO_ACCOUNTS.map(({ label, email: demoEmail }) => (
                      <button key={label} type="button" onClick={() => fillDemo(demoEmail)}
                        className="text-center py-1.5 px-1 rounded-lg border border-[#1e293b] hover:border-emerald-500/40 hover:bg-emerald-900/10 text-[10px] text-[#64748b] hover:text-emerald-400 transition-all">
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#475569] text-center mt-2">Password: <span className="text-[#64748b] font-mono">Demo1234!</span></p>
                </div>

                {/* Divider */}
                <div className="relative flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-[#1e293b]" />
                  <span className="text-xs text-[#475569]">or</span>
                  <div className="flex-1 h-px bg-[#1e293b]" />
                </div>

                {/* Portal access links */}
                <div className="grid grid-cols-2 gap-3 pt-1">
                  {[
                    { label: 'Issuer Portal', href: '/issuer-portal' },
                    { label: 'Compliance Portal', href: '/compliance-portal' },
                    { label: 'Finance Portal', href: '/finance-portal' },
                    { label: 'Admin Portal', href: '/platform-administration-portal' },
                  ].map(({ label, href }) => (
                    <Link key={label} href={href}
                      className="text-center py-2 px-3 rounded-lg border border-[#1e293b] hover:border-[#334155] text-xs text-[#64748b] hover:text-[#94a3b8] transition-all">
                      {label}
                    </Link>
                  ))}
                </div>
              </form>
            )}
          </div>

          {/* Trust strip */}
          <div className="flex items-center justify-center gap-6 mt-6">
            {[
              { icon: Shield, label: 'DFSA Regulated' },
              { icon: Lock, label: '256-bit Encryption' },
            ].map(({ icon: IconComp, label }) => {
              const Icon = IconComp;
              return (
                <div key={label} className="flex items-center gap-1.5 text-xs text-[#475569]">
                  <Icon className="w-3.5 h-3.5 text-emerald-500/60" />
                  {label}
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-[#475569] mt-4">
            Don&apos;t have an account?{' '}
            <Link href="/onboarding" className="text-emerald-400 hover:text-emerald-300 transition-colors">
              Apply for investor access
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
